import type { CSSProperties, MouseEvent } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCubes, faPlay } from "@fortawesome/free-solid-svg-icons";
import type { LessonWorkspaceMode } from "../auth/api-client";
import { fallbackGradient, lessonIconDefinition } from "./lessonIcons";
import { isAdvancedLessonMode, lessonEpicVisualStyle } from "./lessonAdvancedVisual";
import { textColorsForBackground } from "./lessonCardColors";
import { teacherPreviewLessonUrl } from "./teacherPreviewLessonUrl";
import { useLessonLaunch } from "../transition/LessonLaunchProvider";

export interface LessonPreviewTileProps {
  readonly lessonId: string;
  readonly lessonTitle: string;
  readonly courseId?: string;
  readonly cohortId?: string;
  readonly iconName?: string | null;
  readonly iconBgColor?: string | null;
  readonly iconFgColor?: string | null;
  readonly workspaceMode?: LessonWorkspaceMode | null;
  readonly size?: "sm" | "md";
  readonly returnTo?: string;
  readonly communitySource?: boolean;
}

/** Compact lesson icon with hover play, for cohort tables and student lesson lists. */
export function LessonPreviewTile({
  lessonId,
  lessonTitle,
  courseId,
  cohortId,
  iconName,
  iconBgColor,
  iconFgColor,
  workspaceMode,
  size = "sm",
  returnTo,
  communitySource,
}: LessonPreviewTileProps) {
  const launch = useLessonLaunch();
  const epic = lessonEpicVisualStyle({ workspaceMode, baseColor: iconBgColor, fallbackSeed: lessonId });
  const background = epic?.innerBackground ?? iconBgColor?.trim() ?? fallbackGradient(lessonId);
  const colors = textColorsForBackground(background);
  const watermarkColor = iconFgColor?.trim() || colors.title;
  const icon = lessonIconDefinition(iconName);
  const initial = lessonTitle.trim().charAt(0).toUpperCase() || "?";
  const advanced = isAdvancedLessonMode(workspaceMode);
  const previewUrl = teacherPreviewLessonUrl({
    lessonId,
    lessonTitle,
    courseId,
    cohortId,
    iconName,
    iconBgColor,
    iconFgColor,
    workspaceMode,
    ...(returnTo ? { returnTo } : {}),
    ...(communitySource ? { communitySource: true } : {}),
  });

  const onPlay = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    launch({ to: previewUrl, background, watermarkColor, iconDef: icon });
  };

  const tile = (
    <Link
      to={previewUrl}
      onClick={onPlay}
      className={[
        "lesson-preview-tile",
        `lesson-preview-tile--${size}`,
        advanced ? "lesson-preview-tile--advanced" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      data-tone={colors.tone}
      style={{ "--lesson-preview-bg": background } as CSSProperties}
      aria-label={`Preview ${lessonTitle}${advanced ? " (advanced workspace)" : ""}`}
      title={`Preview ${lessonTitle}${advanced ? " · Advanced" : ""}`}
    >
      <span className="lesson-preview-tile-watermark" style={{ color: watermarkColor }} aria-hidden>
        {icon ? <FontAwesomeIcon icon={icon} fixedWidth /> : <span>{initial}</span>}
      </span>
      {advanced ? (
        <span className="lesson-preview-tile-advanced-mark" aria-hidden>
          <FontAwesomeIcon icon={faCubes} />
        </span>
      ) : null}
      <span className="lesson-preview-tile-play" aria-hidden>
        <FontAwesomeIcon icon={faPlay} />
      </span>
    </Link>
  );

  if (!epic) return tile;

  return (
    <span className={`${epic.frameClassName} lesson-preview-tile-epic-wrap`} style={epic.frameStyle}>
      {tile}
    </span>
  );
}
